import { Box, Typography, TextField, Button, CircularProgress, Paper, IconButton, InputAdornment } from "@mui/material";
import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../services/authService";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const EditUser = ({ data, fetch, onClose }) => {
    const [formData, setFormData] = useState({
        username: "",
        password: "",
        email: ""
    });
    const [errors, setErrors] = useState({
        username: "",
        password: "",
        email: ""
    });
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        // console.log(data);
        if (data) {
            setFormData({
                username: data.username || "",
                password: data.password || "",
                email: data.email || ""
            });
        }
    }, [data]);

    const validateusername = (username) => {
        if (!username.trim()) return "Username is required";
        if (username.length < 3) return "Username must be at least 3 characters long";
        if (username.length > 20) return "Username must be less than 20 characters";
        return "";
    };
    const validatePassword = (password) => {
        if (!password.trim()) return ""; // Allow empty passwords
        if (password.length < 8) return "Password must be at least 8 characters long";
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Password must contain at least one special character";
        return "";
    };

    const validateEmail = (email) => {
        if (!email) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return "Please enter a valid email address";
        return "";
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate field
        let errorMessage = "";
        switch (field) {
            case "username":
                errorMessage = validateusername(value);
                break;
            case "password":
                errorMessage = validatePassword(value);
                break;
            case "email":
                errorMessage = validateEmail(value);
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [field]: errorMessage
        }));
    };

    const validateForm = () => {
        const newErrors = {
            username: validateusername(formData.username),
            password: validatePassword(formData.password),
            email: validateEmail(formData.email)
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== "");
    };

    const onUpdateUser = async () => {
        if (!validateForm()) {
            setErrorMsg("Please fix all errors before submitting");
            return;
        }

        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            // Get token from localStorage
            const userData = localStorage.getItem('invCreator');
            if (!userData) {
                throw new Error('Authentication token not found');
            }
            const { token } = JSON.parse(userData);

            // Create payload with only changed fields
            const payload = {};
            if (formData.username !== data.username) payload.username = formData.username;
            if (formData.password !== data.password) payload.password = formData.password;
            if (formData.email !== data.email) payload.email = formData.email;

            // If no fields were changed, show message and return
            if (Object.keys(payload).length === 0) {
                setSuccessMsg("No changes were made");
                setLoading(false);
                return;
            }

            // Make API call with token
            const response = await axios.put(
                `${API_URL}/update_user_by_id/${data.user_id}`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            // console.log(response);

            if (response.statusText === "OK") {
                setSuccessMsg(response.data.message);
                onClose();
                fetch();
            } else {
                setErrorMsg(response.data.message || "Failed to update store");
            }
        } catch (error) {
            console.error("Error updating store:", error);
            setErrorMsg(error.response?.data?.message || "An error occurred while updating the store");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <TextField
                fullWidth
                variant="outlined"
                label="User Name"
                value={formData.username}
                onChange={handleChange('username')}
                error={!!errors.username}
                helperText={errors.username}
                sx={{ mb: 2 }}
                inputProps={{
                    maxLength: 50
                }}
            />

            <TextField
                fullWidth
                variant="outlined"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                margin="normal"
                onChange={handleChange("password")}
                error={!!errors.password}
                disabled={loading}
                helperText={errors.password}
                autoComplete="current-password"
                sx={{ mb: 2 }}
                inputProps={{
                    maxLength: 15,
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={togglePasswordVisibility}
                                edge="end"
                                disabled={loading}
                            >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <TextField
                fullWidth
                variant="outlined"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                sx={{ mb: 2 }}
            />

            {errorMsg && (
                <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
                    {errorMsg}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={onUpdateUser}
                disabled={loading}
                sx={{ height: 48 }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Update Store"}
            </Button>

            {successMsg && (
                <Typography color="success.main" sx={{ textAlign: "center", mt: 2 }}>
                    {successMsg}
                </Typography>
            )}
        </Box>
    );
};

export default EditUser;